import React, { useEffect, useState, useRef } from 'react';
import { Typography, Divider, Card, Table, Row, Col } from 'antd';
import './InDepth.css';
import CarModel from './CarModel'; // Import the CarModel component

const { Title } = Typography;

const predefinedData = {
  BMS: {},
  BPS: {},
  PDB: {},
  MPPT1: {},
  MPPT2: {},
  MC1: {},
  MC2: {},
};

const normalizeKeys = (data) => {
  const normalizedData = {};
  Object.keys(data).forEach((key) => {
    normalizedData[key.toUpperCase()] = data[key];
  });
  return normalizedData;
};

const mergeData = (oldData, newData) => {
  const merged = { ...oldData };
  Object.keys(newData).forEach((key) => {
    if (typeof newData[key] === 'object' && newData[key] !== null && !Array.isArray(newData[key])) {
      merged[key] = mergeData(oldData[key] || {}, newData[key]);
    } else {
      merged[key] = newData[key];
    }
  });
  return merged;
};

const extractTemperatureData = (data) => {
  const temperatureData = {};

  if (data.BMS && data.BMS.high_temp !== undefined) {
    temperatureData.centerRight = data.BMS.high_temp;
  }
  if (data.MPPT2 && data.MPPT2.amb_temp !== undefined) {
    temperatureData.centerRightBackward = data.MPPT2.amb_temp;
  }
  if (data.MPPT1 && data.MPPT1.amb_temp !== undefined) {
    temperatureData.centerRightForward = data.MPPT1.amb_temp;
  }
  if (data.MC1 && data.MC1.motor_temp !== undefined) {
    temperatureData.bottomLeft = data.MC1.motor_temp;
  }
  if (data.MC2 && data.MC2.motor_temp !== undefined) {
    temperatureData.bottomRight = data.MC2.motor_temp;
  }

  return temperatureData;
};

const renderDescriptions = (data) => {
  const items = Object.keys(data).map((key) => {
    const value = data[key];
    return {
      key,
      value: typeof value === 'object' ? renderDescriptions(value) : value.toString()
    };
  });

  return (
    <Table
      dataSource={items}
      columns={[
        {
          title: 'Key',
          dataIndex: 'key',
          key: 'key',
        },
        {
          title: 'Value',
          dataIndex: 'value',
          key: 'value',
        },
      ]}
      pagination={false}
      size="small"
      className="system-table"
    />
  );
};

const InDepth = ({ data }) => {
  const [systemData, setSystemData] = useState(predefinedData);
  const cardRefs = useRef([]);

  useEffect(() => {
    if (data) {
      const normalizedData = normalizeKeys(data);
      setSystemData((prevData) => {
        const mergedData = mergeData(prevData, normalizedData);
        return mergedData;
      });
    }
  }, [data]);

  const temperatureData = extractTemperatureData(systemData);

  const isLargeCard = (system) => {
    const largeCards = ['MC1', 'MC2'];
    return largeCards.includes(system);
  };

  const rows = [];
  let row = [];

  Object.keys(predefinedData).forEach((system) => {
    if (!isLargeCard(system)) {
      row.push(system);
      if (row.length === 3) {
        rows.push(row);
        row = [];
      }
    }
  });

  if (row.length > 0) {
    rows.push(row);
  }

  return (
    <div className="indepth-container">
      <Title level={2} className="text-center">In-Depth System Data</Title>
      <Divider />
      <CarModel temperatureData={temperatureData} /> {/* Add the CarModel component */}
      <Divider className="section-divider" />
      {rows.map((row, rowIndex) => (
        <Row gutter={[16, 16]} key={rowIndex} className="card-row">
          {row.map((system, colIndex) => (
            <Col span={8} key={system}>
              <Card
                className="system-card"
                ref={(el) => (cardRefs.current[rowIndex * 3 + colIndex] = el)}
              >
                <div className="card-header">
                  <Title className="system-title" level={4}>{system}</Title>
                </div>
                <Divider className="title-divider" />
                {renderDescriptions(systemData[system] || {})}
              </Card>
            </Col>
          ))}
        </Row>
      ))}
      <Row gutter={[16, 16]} className="large-card-row">
        {['MC1', 'MC2'].map((system) => (
          <Col span={12} key={system}>
            <Card className="system-card">
              <div className="card-header">
                <Title className="system-title" level={4}>{system}</Title>
              </div>
              <Divider className="title-divider" />
              {renderDescriptions(systemData[system] || {})}
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default InDepth;
